import type { CampaignCreationTask } from "@adpilot/shared";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Progress, ProgressIndicator, ProgressTrack } from "@/components/ui/progress";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { usePermission } from "@/hooks/use-permission";
import { campaignCreationTaskStageKey } from "@/lib/campaign-creation-tasks";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth";

export interface CampaignCreationProgressProps {
  tasks: CampaignCreationTask[];
  collapsed: boolean;
  reconnecting: boolean;
  hasMore: boolean;
}

const COMPLETION_VISIBLE_MS = 5_000;
const RECENT_SIMULATION_MS = 60_000;
const taskPriority: Record<CampaignCreationTask["status"], number> = {
  needs_attention: 0,
  blocked: 0,
  failed: 0,
  running: 1,
  queued: 2,
  completed: 3,
  simulated: 3,
  canceled: 4,
};

function isFinished(task: CampaignCreationTask) {
  return task.status === "completed" || task.status === "simulated";
}

export function CampaignCreationProgress({
  tasks,
  collapsed,
  reconnecting,
  hasMore,
}: CampaignCreationProgressProps): React.ReactElement | null {
  const { t } = useTranslation("layout");
  const navigate = useNavigate();
  const shops = useAuthStore((state) => state.shops);
  const setCurrentShop = useAuthStore((state) => state.setCurrentShop);
  const { hasModule } = usePermission();
  const [completionDeadlines, setCompletionDeadlines] = useState<Record<string, number>>({});
  const [now, setNow] = useState(Date.now);

  useEffect(() => {
    setCompletionDeadlines((previous) => {
      const newlyFinished = tasks.filter((task) => isFinished(task) && !previous[task.id]);
      if (newlyFinished.length === 0) return previous;
      const current = Date.now();
      return {
        ...previous,
        ...Object.fromEntries(
          newlyFinished.map((task) => [
            task.id,
            // Completions that predate this page load (e.g. from an earlier session) are not
            // news; the minute-wide window only absorbs client/server clock skew.
            task.finishedAt !== null &&
            Date.parse(task.finishedAt) <= current - RECENT_SIMULATION_MS
              ? 0
              : current + COMPLETION_VISIBLE_MS,
          ]),
        ),
      };
    });
  }, [tasks]);

  useEffect(() => {
    const nextDeadline = Math.min(
      ...Object.values(completionDeadlines).filter((deadline) => deadline > now),
    );
    if (!Number.isFinite(nextDeadline)) return;
    const timer = window.setTimeout(
      () => setNow(Date.now()),
      Math.max(0, nextDeadline - Date.now()),
    );
    return () => window.clearTimeout(timer);
  }, [completionDeadlines, now]);

  const visibleTasks = tasks
    .filter(
      (task) =>
        task.status !== "canceled" &&
        (!isFinished(task) ||
          task.executionMode === "amazon" ||
          (task.finishedAt !== null && Date.parse(task.finishedAt) > now - RECENT_SIMULATION_MS)) &&
        (!isFinished(task) || (completionDeadlines[task.id] ?? Number.POSITIVE_INFINITY) > now),
    )
    .sort(
      (left, right) =>
        taskPriority[left.status] - taskPriority[right.status] ||
        right.createdAt.localeCompare(left.createdAt),
    );
  const task = visibleTasks[0];
  if (!task) return null;

  const needsAttention = ["blocked", "needs_attention", "failed"].includes(task.status);
  const simulated = task.executionMode !== "amazon" || task.status === "simulated";
  const liveCompleted =
    task.status === "completed" && !simulated && task.identityVerification === "verified";
  const stageLabel = t(`creationProgress.${campaignCreationTaskStageKey(task)}`);
  const stage =
    simulated && !isFinished(task)
      ? t("creationProgress.simulationStage", { stage: stageLabel })
      : stageLabel;
  const shopName = shops.find((shop) => shop.shopId === task.shopId)?.shopName;
  const summary = shopName ? t("creationProgress.summary", { shopName, stage }) : stage;
  const count = task.counts.campaigns;
  const countDetail =
    !isFinished(task) && !needsAttention && count.total > 0
      ? task.phase === "activation"
        ? t("creationProgress.activatedCampaigns", { count: count.active, total: count.total })
        : task.phase === "hierarchy"
          ? t("creationProgress.createdCampaigns", { count: count.created, total: count.total })
          : null
      : null;
  const canOpenGroup = liveCompleted && task.managedGroupId && hasModule("ai");
  const actionLabel = canOpenGroup
    ? t("creationProgress.openGroup")
    : t("creationProgress.openCreation");
  const openTask = () => {
    setCurrentShop(task.shopId);
    if (canOpenGroup && task.managedGroupId) {
      void navigate({ to: "/managed-groups/$id", params: { id: task.managedGroupId } });
    } else {
      void navigate({
        to: "/campaigns",
        search: (previous) => ({ ...previous, smartCreation: undefined, creationTask: task.id }),
      });
    }
  };

  return (
    <div
      className="campaign-creation-progress relative h-7 w-full shrink-0 rounded-[10px]"
      data-animated={
        !reconnecting && ["queued", "running"].includes(task.status) ? "true" : "false"
      }
      data-status={task.status}
    >
      <Tooltip>
        <TooltipTrigger
          render={
            <Button
              variant="ghost"
              size="xs"
              className="absolute inset-0 h-7 w-full rounded-[10px] p-0"
              onClick={openTask}
              aria-label={`${summary}. ${actionLabel}`}
            />
          }
        />
        <TooltipContent side="right" sideOffset={8} className="motion-reduce:animate-none">
          <span className="flex min-w-0 flex-col gap-1 break-words leading-4">
            <span>{summary}</span>
            {countDetail && <span>{countDetail}</span>}
            {needsAttention && task.lastError && <span>{task.lastError.message}</span>}
            {visibleTasks.length > 1 && (
              <span>
                {hasMore
                  ? t("creationProgress.moreTasks", { count: visibleTasks.length - 1 })
                  : t("creationProgress.otherTasks", { count: visibleTasks.length - 1 })}
              </span>
            )}
            {reconnecting && <span>{t("creationProgress.reconnecting")}</span>}
            <span>{actionLabel}</span>
          </span>
        </TooltipContent>
      </Tooltip>
      <Progress
        // Task phases have unequal costs; only a terminal fact has a known percentage.
        value={isFinished(task) ? 100 : null}
        aria-label={summary}
        getAriaValueText={() => summary}
        className={cn(
          "pointer-events-none absolute top-1/2 w-auto -translate-y-1/2",
          collapsed ? "inset-x-1.5" : "inset-x-2.5",
        )}
      >
        <ProgressTrack tone="success" className="h-2">
          <ProgressIndicator tone="success" className="h-full w-full" />
        </ProgressTrack>
      </Progress>
      <span className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {summary}
      </span>
    </div>
  );
}
